import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  TrendingUp, TrendingDown, Users, BarChart3, Activity,
  Loader2, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';

// ===================== CONSTANTS =====================

const COLORS = [
  '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444',
  '#EC4899', '#06B6D4', '#6366F1', '#14B8A6', '#F97316',
];

const EVENTS = [
  { value: 'any_pageview', label: 'Any Page View' },
  { value: 'Canvas', label: 'Canvas' },
  { value: 'Co-pilot', label: 'Co-pilot' },
  { value: 'Features', label: 'Features' },
  { value: 'Releases', label: 'Releases' },
  { value: 'Goals / KPIs', label: 'Goals / KPIs' },
  { value: 'Research', label: 'Research' },
  { value: 'Competitors', label: 'Competitors' },
  { value: 'Feedback', label: 'Feedback' },
  { value: 'Settings', label: 'Settings' },
  { value: 'Products', label: 'Products' },
  { value: 'Dev Log', label: 'Dev Log' },
];

const GROUP_OPTIONS = [
  { value: 'none', label: 'No grouping' },
  { value: 'device', label: 'Device Type' },
  { value: 'browser', label: 'Browser' },
  { value: 'country', label: 'Country' },
  { value: 'os', label: 'OS' },
  { value: 'feature', label: 'Feature' },
];

const MEASURE_OPTIONS = [
  { value: 'unique_users', label: 'Unique Users' },
  { value: 'total_events', label: 'Total Events' },
  { value: 'events_per_user', label: 'Events / User' },
];

// ===================== TOOLTIPS =====================

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name || 'Value'}:</span>
          <span className="text-foreground font-medium">{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

// ===================== STAT CARD =====================

const StatCard = ({ label, value, icon: Icon, color = '#3B82F6', colors }) => (
  <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className={cn("text-xs font-medium uppercase tracking-wide", colors.textMuted)}>{label}</p>
          <p className={cn("text-2xl font-bold mt-1", colors.textPrimary)}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        <div className="p-2.5 rounded-lg" style={{ background: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ===================== SPARKLINE =====================

const Sparkline = ({ data, color = '#3B82F6', width = 80, height = 24 }) => {
  if (!data?.length) return <Minus className="w-4 h-4 text-muted-foreground" />;
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - (v / max) * height}`).join(' ');
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
    </svg>
  );
};

// ===================== SUB VIEWS =====================

const EventSegmentation = ({ days, colors }) => {
  const [event, setEvent] = useState('any_pageview');
  const [groupBy, setGroupBy] = useState('none');
  const [measure, setMeasure] = useState('unique_users');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, event, measure });
      if (groupBy !== 'none') params.set('group_by', groupBy);
      const res = await api.get(`/api/admin/metrics/event-segmentation?${params}`);
      setData(res.data);
    } catch (err) {
      console.error('Event segmentation error:', err);
    } finally {
      setLoading(false);
    }
  }, [days, event, groupBy, measure]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const groups = data?.groups || [];
  const hasGroups = groups.length > 0;
  const measureLabel = MEASURE_OPTIONS.find(m => m.value === measure)?.label || measure;

  return (
    <div className="space-y-4">
      {/* Controls bar */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <ControlSelect label="Event" value={event} onChange={setEvent} options={EVENTS} colors={colors} />
            <ControlSelect label="Group by" value={groupBy} onChange={setGroupBy} options={GROUP_OPTIONS} colors={colors} />
            <ControlSelect label="Measured as" value={measure} onChange={setMeasure} options={MEASURE_OPTIONS} colors={colors} />
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      {data && !loading && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Unique Users" value={data.total_unique_users} icon={Users} color="#3B82F6" colors={colors} />
          <StatCard label="Total Events" value={data.total_events} icon={Activity} color="#8B5CF6" colors={colors} />
        </div>
      )}

      {/* Chart */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardContent className="p-5">
          <h3 className={cn("text-sm font-medium mb-4", colors.textPrimary)}>{measureLabel} over time</h3>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              {hasGroups ? (
                <AreaChart data={data.timeline}>
                  <defs>
                    {groups.map((g, i) => (
                      <linearGradient key={g} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {groups.map((g, i) => (
                    <Area key={g} type="monotone" dataKey={g} name={g}
                      stroke={COLORS[i % COLORS.length]} fill={`url(#grad-${i})`}
                      strokeWidth={2} dot={false} />
                  ))}
                </AreaChart>
              ) : (
                <AreaChart data={data.timeline}>
                  <defs>
                    <linearGradient id="gradMain" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="value" name={measureLabel}
                    stroke="#3B82F6" fill="url(#gradMain)" strokeWidth={2} dot={false} />
                </AreaChart>
              )}
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Breakdown table */}
      {data?.breakdown?.length > 0 && (
        <Card className={cn("border overflow-hidden", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="py-3 px-5 border-b">
            <CardTitle className={cn("text-sm font-medium", colors.textPrimary)}>Breakdown</CardTitle>
          </CardHeader>
          <table className="w-full">
            <thead>
              <tr className={cn("text-xs uppercase tracking-wider border-b", colors.textMuted)}>
                <th className="text-left px-5 py-3">{groupBy !== 'none' ? groupBy : 'Segment'}</th>
                <th className="text-right px-5 py-3">Unique Users</th>
                <th className="text-right px-5 py-3">Total Events</th>
                <th className="text-right px-5 py-3">Events / User</th>
              </tr>
            </thead>
            <tbody>
              {data.breakdown.map((row, i) => (
                <tr key={i} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className={cn("text-sm", colors.textPrimary)}>{row.group}</span>
                    </div>
                  </td>
                  <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{row.unique_users.toLocaleString()}</td>
                  <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{row.total_events.toLocaleString()}</td>
                  <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{row.events_per_user}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

const FeatureAdoption = ({ days, colors }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/admin/metrics/feature-adoption?days=${days}`);
        setData(res.data);
      } catch (err) {
        console.error('Feature adoption error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [days]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>;
  if (!data?.features?.length) return <p className={cn("text-center py-12", colors.textMuted)}>No feature usage data yet.</p>;

  const maxUsers = Math.max(...data.features.map(f => f.unique_users), 1);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Active Users" value={data.total_active_users} icon={Users} color="#3B82F6" colors={colors} />
        <StatCard label="Features Tracked" value={data.features.length} icon={BarChart3} color="#8B5CF6" colors={colors} />
      </div>

      {/* Feature bars */}
      <Card className={cn("border overflow-hidden", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="py-3 px-5 border-b">
          <CardTitle className={cn("text-sm font-medium", colors.textPrimary)}>Feature Adoption - Unique Users</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          {data.features.map((f, i) => (
            <div key={f.feature}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-3">
                  <span className={cn("text-sm font-medium w-28", colors.textPrimary)}>{f.feature}</span>
                  <span className={cn("text-xs", colors.textMuted)}>{f.unique_users} users</span>
                  <span className={cn("text-xs", colors.textMuted)}>{f.dau_percentage}% of active</span>
                </div>
                <div className="flex items-center gap-3">
                  <Sparkline data={f.trend_data?.map(d => d.users) || []} color={COLORS[i % COLORS.length]} />
                  <TrendBadge value={f.trend_pct} />
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${(f.unique_users / maxUsers) * 100}%`, background: COLORS[i % COLORS.length] }}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Detail table */}
      <Card className={cn("border overflow-hidden", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="py-3 px-5 border-b">
          <CardTitle className={cn("text-sm font-medium", colors.textPrimary)}>Detail</CardTitle>
        </CardHeader>
        <table className="w-full">
          <thead>
            <tr className={cn("text-xs uppercase tracking-wider border-b", colors.textMuted)}>
              <th className="text-left px-5 py-3">Feature</th>
              <th className="text-right px-5 py-3">Unique Users</th>
              <th className="text-right px-5 py-3">Total Visits</th>
              <th className="text-right px-5 py-3">Avg Time (s)</th>
              <th className="text-right px-5 py-3">% of Active</th>
              <th className="text-right px-5 py-3">Trend</th>
            </tr>
          </thead>
          <tbody>
            {data.features.map((f, i) => (
              <tr key={f.feature} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className={cn("text-sm", colors.textPrimary)}>{f.feature}</span>
                  </div>
                </td>
                <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{f.unique_users}</td>
                <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{f.total_visits}</td>
                <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{f.avg_time_seconds}</td>
                <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{f.dau_percentage}%</td>
                <td className="text-right px-5 py-3"><TrendBadge value={f.trend_pct} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

const RetentionAnalysis = ({ days, colors }) => {
  const [interval, setInterval] = useState('weekly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const weeks = useMemo(() => {
    if (interval === 'daily') return Math.min(Math.ceil(days / 1), 28);
    if (interval === 'monthly') return Math.min(Math.ceil(days / 30), 12);
    return Math.min(Math.ceil(days / 7), 12);
  }, [days, interval]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/admin/metrics/retention?weeks=${weeks}&interval=${interval}`);
        setData(res.data);
      } catch (err) {
        console.error('Retention error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [weeks, interval]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>;

  const periodLabel = interval === 'daily' ? 'Day' : interval === 'monthly' ? 'Month' : 'Week';

  return (
    <div className="space-y-4">
      {/* Controls */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <ControlSelect label="Interval" value={interval} onChange={setInterval} options={[
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'monthly', label: 'Monthly' },
            ]} colors={colors} />
          </div>
        </CardContent>
      </Card>

      {/* Average retention curve */}
      {data?.average_retention?.length > 1 && (
        <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
          <CardContent className="p-5">
            <h3 className={cn("text-sm font-medium mb-4", colors.textPrimary)}>Average Retention Curve</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.average_retention.map((v, i) => ({ period: `${periodLabel} ${i}`, pct: v }))}>
                <defs>
                  <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} domain={[0, 100]} unit="%" />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="pct" name="Retention %" stroke="#10B981" fill="url(#retGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Cohort heatmap table */}
      {data?.cohorts?.length > 0 ? (
        <Card className={cn("border overflow-hidden", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="py-3 px-5 border-b">
            <CardTitle className={cn("text-sm font-medium", colors.textPrimary)}>Cohort Retention Heatmap</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={cn("text-xs uppercase tracking-wider border-b", colors.textMuted)}>
                  <th className={cn("text-left px-4 py-3 whitespace-nowrap sticky left-0 z-10", colors.bgCard)}>Cohort</th>
                  <th className="text-center px-3 py-3 whitespace-nowrap">Size</th>
                  {Array.from({ length: data.max_periods }, (_, i) => (
                    <th key={i} className="text-center px-3 py-3 whitespace-nowrap">{periodLabel} {i}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.cohorts.map((cohort) => (
                  <tr key={cohort.cohort} className="border-b border-border">
                    <td className={cn("px-4 py-2 text-sm whitespace-nowrap sticky left-0 z-10", colors.textPrimary, colors.bgCard)}>
                      {cohort.cohort}
                    </td>
                    <td className={cn("text-center px-3 py-2 text-sm", colors.textMuted)}>{cohort.size}</td>
                    {Array.from({ length: data.max_periods }, (_, i) => {
                      const val = cohort.retention[i];
                      if (val === undefined) return <td key={i} className="px-3 py-2" />;
                      return (
                        <td key={i} className="px-3 py-2 text-center">
                          <div
                            className="mx-auto rounded px-2 py-1 text-xs font-medium min-w-[48px]"
                            style={{ background: getRetentionColor(val), color: val > 50 ? '#fff' : '#1f2937' }}
                            title={i === 0 ? `${cohort.size} users` : `${Math.round(cohort.size * val / 100)} of ${cohort.size}`}
                          >
                            {val}%
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <p className={cn("text-center py-12", colors.textMuted)}>No cohort data available yet.</p>
      )}
    </div>
  );
};

const UserActivity = ({ days, colors }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/admin/metrics/user-activity?days=${days}`);
        setData(res.data);
      } catch (err) {
        console.error('User activity error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [days]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <StatCard label="Active Users" value={data.total_active_users} icon={Users} color="#3B82F6" colors={colors} />

      {/* Top users table */}
      <Card className={cn("border overflow-hidden", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="py-3 px-5 border-b">
          <CardTitle className={cn("text-sm font-medium", colors.textPrimary)}>Most Active Users</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={cn("text-xs uppercase tracking-wider border-b", colors.textMuted)}>
                <th className="text-left px-5 py-3">User</th>
                <th className="text-left px-5 py-3">Organization</th>
                <th className="text-right px-3 py-3">Sessions</th>
                <th className="text-right px-3 py-3">Pages</th>
                <th className="text-left px-3 py-3">Top Feature</th>
                <th className="text-center px-3 py-3">Activity (14d)</th>
                <th className="text-right px-5 py-3">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {data.users.slice(0, 25).map((u) => (
                <tr key={u.user_id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="px-5 py-3">
                    <div>
                      <div className={cn("text-sm", colors.textPrimary)}>{u.name || '-'}</div>
                      <div className={cn("text-xs", colors.textMuted)}>{u.email}</div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className={cn("text-sm", colors.textPrimary)}>{u.org_name || '-'}</div>
                    {u.plan && <div className={cn("text-xs", colors.textMuted)}>{u.plan}</div>}
                  </td>
                  <td className={cn("text-right px-3 py-3 text-sm font-medium", colors.textPrimary)}>{u.sessions}</td>
                  <td className={cn("text-right px-3 py-3 text-sm", colors.textPrimary)}>{u.pageviews}</td>
                  <td className={cn("px-3 py-3 text-sm", colors.textPrimary)}>{u.top_feature}</td>
                  <td className="px-3 py-3 text-center"><Sparkline data={u.sparkline} color="#3B82F6" /></td>
                  <td className={cn("text-right px-5 py-3 text-xs whitespace-nowrap", colors.textMuted)}>
                    {u.last_seen ? new Date(u.last_seen).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Top orgs */}
      {data.top_orgs?.length > 0 && (
        <Card className={cn("border overflow-hidden", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="py-3 px-5 border-b">
            <CardTitle className={cn("text-sm font-medium", colors.textPrimary)}>Most Active Organizations</CardTitle>
          </CardHeader>
          <table className="w-full">
            <thead>
              <tr className={cn("text-xs uppercase tracking-wider border-b", colors.textMuted)}>
                <th className="text-left px-5 py-3">Organization</th>
                <th className="text-left px-5 py-3">Plan</th>
                <th className="text-right px-5 py-3">Active Users</th>
                <th className="text-right px-5 py-3">Sessions</th>
                <th className="text-right px-5 py-3">Pageviews</th>
              </tr>
            </thead>
            <tbody>
              {data.top_orgs.slice(0, 10).map((o) => (
                <tr key={o.org_id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className={cn("px-5 py-3 text-sm", colors.textPrimary)}>{o.name}</td>
                  <td className={cn("px-5 py-3 text-sm", colors.textMuted)}>{o.plan || '-'}</td>
                  <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{o.active_users}</td>
                  <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{o.sessions}</td>
                  <td className={cn("text-right px-5 py-3 text-sm", colors.textPrimary)}>{o.pageviews}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

// ===================== HELPERS =====================

const TrendBadge = ({ value }) => {
  if (value === 0 || value === undefined) return <span className="text-xs text-muted-foreground">-</span>;
  const positive = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${positive ? 'text-emerald-500' : 'text-red-500'}`}>
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {positive ? '+' : ''}{value}%
    </span>
  );
};

const ControlSelect = ({ label, value, onChange, options, colors }) => (
  <div className="flex items-center gap-2">
    <span className={cn("text-xs uppercase tracking-wider", colors.textMuted)}>{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn("h-8 w-auto min-w-[140px] text-sm", colors.inputBg)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => (
          <SelectItem key={o.value} value={o.value} className="text-sm">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

function getRetentionColor(pct) {
  if (pct >= 80) return '#059669';
  if (pct >= 60) return '#10B981';
  if (pct >= 40) return '#22C55E';
  if (pct >= 25) return '#84CC16';
  if (pct >= 15) return '#EAB308';
  if (pct >= 8) return '#F59E0B';
  if (pct >= 3) return '#F97316';
  return '#EF4444';
}

// ===================== MAIN COMPONENT =====================

const ANALYTICS_VIEWS = [
  { value: 'segmentation', label: 'Event Segmentation', icon: BarChart3 },
  { value: 'adoption', label: 'Feature Adoption', icon: Activity },
  { value: 'retention', label: 'Retention', icon: TrendingUp },
  { value: 'users', label: 'User Activity', icon: Users },
];

const AdminAnalyticsTab = ({ period }) => {
  const [view, setView] = useState('segmentation');
  const days = parseInt(period) || 30;
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  return (
    <div className="space-y-4">
      {/* Top bar: sub-view selector */}
      <div className="flex items-center">
        <div className={cn("flex gap-1 border rounded-lg p-1", colors.bgCard, colors.borderPrimary)}>
          {ANALYTICS_VIEWS.map(v => (
            <button
              key={v.value}
              data-testid={`analytics-view-${v.value}`}
              onClick={() => setView(v.value)}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors",
                view === v.value
                  ? 'bg-blue-500/20 text-blue-600 font-medium'
                  : cn(colors.textMuted, 'hover:bg-muted/50')
              )}
            >
              <v.icon className="w-3.5 h-3.5" />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active view */}
      {view === 'segmentation' && <EventSegmentation days={days} colors={colors} />}
      {view === 'adoption' && <FeatureAdoption days={days} colors={colors} />}
      {view === 'retention' && <RetentionAnalysis days={days} colors={colors} />}
      {view === 'users' && <UserActivity days={days} colors={colors} />}
    </div>
  );
};

export default AdminAnalyticsTab;
